<template>
  <div class="dropdown-main" :style="{ opacity: disabled ? '0.6' : '1' }">
    <div v-if="!hidePlaceholder" class="dropdown-label">
      {{ placeholder }} <span v-if="isRequired" class="error-text">**</span>
      {{
        dropdownItemsFiltered.length != items.length &&
        !isCategorised &&
        !noFilter
          ? `(Showing ${dropdownItemsFiltered.length} of ${items.length} items)`
          : ""
      }}
    </div>
    <div
      class="dropdown-container"
      :tabindex="disabled || searchable ? '-1' : '0'"
      @keydown="dropdownKeydown"
      @click="dropdownClicked"
      @blur="dropdownFocusOut"
      ref="dropdownContainerEl"
    >
      <div v-if="searchable && selectedObject" class="dropdown-search-selected">
        {{ selectedObject.value }}
      </div>
      <input
        v-if="searchable"
        class="dropdown-search-input"
        type="text"
        placeholder="Search to select"
        @focus="showDropdown = true"
        @input="searchInput"
        ref="searchInputEl"
        @click="!showDropdown ? (showDropdown = true) : ''"
        :disabled="disabled"
      />
      <div v-else-if="selectedObject">{{ selectedObject.value }}</div>
      <div v-else class="dropdown-placeholder">Please Select</div>

      <img class="dropdown-icon" src="../assets/dropdown/dropdown.png" alt="" />
    </div>

    <div
      v-if="showDropdown"
      class="dropdown-select-container"
      ref="dropdownSelectEl"
    >
      <template v-if="!isCategorised">
        <div
          v-for="(item,ind) in (noFilter ? items : dropdownItemsFiltered) as ItemBase[]"
          :key="ind"
          class="dropdown-item"
          @click="itemClicked(item)"
          @mouseenter="dropdownItemHover = true"
          @mouseleave="dropdownItemHover = false"
          ref="dropdownItemEl"
        >
          {{ item.value }}
        </div>
      </template>
      <template v-else>
        <template
          v-for="(item,ind) in (noFilter ? items : dropdownItemsFiltered) as ItemBase[][]"
          :key="ind"
        >
          <div
            v-if="item.find((it: ItemBase) => !it.hide)"
            class="dropdown-category"
          >
            {{ item[0].category }}
          </div>
          <template
            v-for="(catItem, catInd) in item as ItemBase[]"
            :key="catInd"
          >
            <div
              v-if="!catItem.hide"
              class="dropdown-item dropdown-category-item"
              ref="dropdownItemEl"
              @mouseenter="dropdownItemHover = true"
              @mouseleave="dropdownItemHover = false"
              @click="itemClicked(catItem)"
            >
              {{ catItem.value }}
            </div>
          </template>
        </template>
      </template>
    </div>

    <div class="disabled-container" v-if="disabled"></div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  withDefaults,
  defineProps,
  nextTick,
  watch,
  onMounted,
  defineEmits,
  VNodeRef,
} from "vue";
import { DropdownProps, ItemBase } from "./componentModel/DropdownModel";

const props = withDefaults(defineProps<DropdownProps<ItemBase>>(), {
  placeholder: "Label",
  position: "bottom",
  disabled: false,
  searchable: false,
  isRequired: false,
  hidePlaceholder: false,
  noFilter: false,
});
const emit = defineEmits([
  "update:selected", // To handle 2 way binding for the selected when selectedObject changes
  "onChange",
]);

//#region Data
const showDropdown = ref(false); // To show or hide the dropdown
const dropdownContainerEl = ref<VNodeRef | null>(null); // The container for the dropdown
const dropdownItemEl = ref<VNodeRef[] | null>(null); // The element for the dropdown item
const dropdownSelectEl = ref<VNodeRef | null>(null); // The container element for the dropdown element
const dropdownItemHover = ref(false); // When hovering on the item to make sure that the item is clickable
const dropdownIsClick = ref(false); // When clicking on the dropdown
const dropdownPositionSet = ref("unset"); // To set the dropdown position either top or bottom based on the screen height
const selectedObject = ref<ItemBase | null>(null); // Private selected object, bind to selected props

const searchText = ref(""); // The text that the user is searching
const searchTimeout = ref<any>(null); // The timeout of the searching
const dropdownItemsFiltered = ref<ItemBase[] | ItemBase[][]>([]); // The filtered items of the dropdown
const currentObjectIndex = ref(-1); // The current dropdown index based on the keydown or arrow
const searchInputEl = ref<VNodeRef | null>(null);

const isCategorised = ref<boolean>(false); // To indicate that the dropdown is a categorised dropdown
//#endregion Data

//#region Methods
const itemClicked = (item: ItemBase) => {
  selectedObject.value = item;
  showDropdown.value = false;

  dropdownContainerEl.value.focus();

  dropdownItemHover.value = false;

  if (props.searchable) {
    searchInputEl.value.value = selectedObject.value.value;
  }
};
const dropdownKeydown = async (e: KeyboardEvent) => {
  // Not to do anything if the dropdown is not shown
  // if (!showDropdown.value) {
  //   return;
  // }

  if (e.key == " " && !props.searchable) {
    e.preventDefault();
    showDropdown.value = !showDropdown.value;

    await nextTick();
    if (showDropdown.value) {
      highlightDropdownItem();
    }
  } else if (e.key == "Enter") {
    if (currentObjectIndex.value == -1) {
      if (dropdownItemsFiltered.value.length == 1) {
        currentObjectIndex.value = 0;
      } else {
        return;
      }
    }

    if (props.searchable) {
      // Set the selected object
      selectedObject.value = (dropdownItemsFiltered.value as ItemBase[])[
        currentObjectIndex.value
      ];
      // Hide the dropdown
      showDropdown.value = false;

      // Reset the dropdown list
      dropdownItemsFiltered.value = props.items;
      // Set the value of the input
      searchInputEl.value.value = selectedObject.value.value;
    } else {
      showDropdown.value = false;
    }
  } else if (e.key == "ArrowUp" || e.key == "ArrowDown") {
    e.preventDefault();

    if (e.key == "ArrowUp") {
      currentObjectIndex.value -= 1;
    } else if (e.key == "ArrowDown") {
      currentObjectIndex.value += 1;
    }

    if (currentObjectIndex.value < 0) {
      currentObjectIndex.value = dropdownItemsFiltered.value.length - 1;
    } else if (currentObjectIndex.value >= dropdownItemsFiltered.value.length) {
      currentObjectIndex.value = 0;
    }

    if (!props.searchable) {
      selectedObject.value = (dropdownItemsFiltered.value as ItemBase[])[
        currentObjectIndex.value
      ];
    }

    if (showDropdown.value) {
      highlightDropdownItem(currentObjectIndex.value);
    }
  } else if (e.key.length == 1 && !props.searchable) {
    // Setting the search text
    searchText.value += e.key;

    // Clear the timeout
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value);
    }

    // Set the timeout
    searchTimeout.value = setTimeout(() => {
      // Find the search text in the list
      let searchResult = props.items.find((i) => {
        let regex = new RegExp(`^${searchText.value}`, "gi");
        let match = i.value.match(regex);

        return match;
      });

      // Found result in the search result
      if (searchResult) {
        selectedObject.value = searchResult;
        // Move the selected item in the dropdown if shown
        if (showDropdown.value) {
          highlightDropdownItem();
        }
      }

      // Clear the search
      searchText.value = "";
      clearTimeout(searchTimeout.value);
    }, 250);
  }
};
const dropdownClicked = async () => {
  if (!props.searchable) {
    showDropdown.value = !showDropdown.value;
    dropdownIsClick.value = true;
    await nextTick();
    highlightDropdownItem();
  }
};
const dropdownFocusOut = () => {
  if (!dropdownItemHover.value && !dropdownIsClick.value) {
    showDropdown.value = false;
  } else if (dropdownIsClick.value && dropdownContainerEl.value) {
    dropdownIsClick.value = false;
    dropdownContainerEl.value.focus();
  }
};
const highlightDropdownItem = (ind?: number) => {
  if (selectedObject.value) {
    ind = props.items.findIndex(
      (i) => i.value == selectedObject.value?.value
    );
  } else if (props.searchable) {
    //
  }

  dropdownItemEl.value?.forEach((i: any) =>
    (i as HTMLElement).classList.remove("dropdown-selected")
  );

  let currentElement = dropdownItemEl.value?.at(ind ?? 0) as
    | HTMLElement
    | undefined;
  currentElement?.classList.add("dropdown-selected");
  currentElement?.scrollIntoView({
    block: "nearest",
  });
};
// When the search input is being typed
const searchInput = (e: Event) => {
  // Get the value and filter based on all items
  const searchText = (e.target as any).value;
  dropdownItemsFiltered.value = props.items.filter((i) =>
    i.value.toLowerCase().includes(searchText.toLowerCase())
  );

  // Clear the selectedobject if already selected
  if (selectedObject.value) {
    selectedObject.value = null;
    currentObjectIndex.value = -1;
  }

  // To open the dropdown if it is not opened
  if (!showDropdown.value) {
    showDropdown.value = true;
  }
};
//#endregion Methods

//#region Lifecycle
onMounted(() => {
  if (props.selected) {
    selectedObject.value = props.selected;
  }

  if (props.items && props.items?.length > 0) {
    if (props.items[0].category) {
      isCategorised.value = true;

      const allCategories = [
        ...new Set(props.items.map((item: ItemBase) => item.category)),
      ];
      const tempData: ItemBase[][] = [];

      allCategories.forEach((cat: string | undefined) => {
        tempData.push(
          props.items.filter((item: ItemBase) => item.category == cat)
        );
      });

      dropdownItemsFiltered.value = tempData;
    } else {
      isCategorised.value = false;
      dropdownItemsFiltered.value = props.items;
    }
  }
});
//#endregion Lifecycle

//#region Watchers
watch(showDropdown, async (newVal) => {
  if (newVal) {
    await nextTick();
    if (!props.position) {
      let pageHeight = window.innerHeight;
      let dropdownSelectBottom =
        dropdownSelectEl.value.getBoundingClientRect().bottom;

      if (pageHeight <= dropdownSelectBottom) {
        dropdownSelectEl.value.classList.add("dropdown-select-container-top");
        dropdownPositionSet.value = "top";
      }
    } else if (props.position == "top") {
      dropdownSelectEl.value.classList.add("dropdown-select-container-top");
    }

    dropdownSelectEl.value.focus();

    // To reset the dropdown index
    currentObjectIndex.value = -1;
    // To set the current index to the selected object if an object is already selected
    if (selectedObject.value) {
      if (!isCategorised.value) {
        // For non categorised item
        currentObjectIndex.value = (
          dropdownItemsFiltered.value as ItemBase[]
        ).findIndex((i) => i.value == selectedObject.value?.value);
      } else {
        // For categorised items
        // Default cat index at 0
        let catIndex = 0;
        (dropdownItemsFiltered.value as ItemBase[][]).forEach(
          (category: ItemBase[]) => {
            // Get the index by the category
            const index = category.findIndex(
              (catItem: ItemBase) =>
                selectedObject.value?.category == catItem.category &&
                selectedObject.value?.value == catItem.value
            );
            // Set the object index based on the category index and the index found (more or equals to 0 means found)
            if (index >= 0) {
              currentObjectIndex.value = catIndex + index;
            }
            // Add the category index with the current length of the category
            catIndex += category.length;
          }
        );
      }

      highlightDropdownItem(currentObjectIndex.value);
    }
  }
});
watch(selectedObject, (val) => {
  // Handle when the selected object changes and update the selected prop for 2 way binding
  emit("update:selected", val);
  emit("onChange", val);
});
watch(
  () => props.selected,
  (val) => {
    selectedObject.value = val;
  }
);
//#endregion Watchers
</script>

<style scoped>
.dropdown-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  position: relative;
}
.dropdown-container {
  background-color: rgba(211, 211, 211, 0.2);
  border: 2px solid rgba(211, 211, 211, 0.2);
  border-radius: 5px;
  cursor: pointer;
  position: relative;
  width: 100%;
  min-height: 30px;
  padding: 5px 10px;
  display: flex;
}
.dropdown-container:focus {
  border: 2px solid gray;
  outline: none;
}
.dropdown-placeholder {
  font-style: italic;
  color: gray;
}
.dropdown-select-container {
  position: absolute;
  top: 100%;
  width: 100%;
  max-height: 200px;
  overflow: auto;
  border: 2px solid gray;
  background-color: white;
  z-index: 99;
}
.dropdown-select-container-top {
  top: unset;
  bottom: calc(100% - 20px);
}
.dropdown-category {
  padding: 5px;
  font-weight: bold;
  pointer-events: none;
  position: sticky;
  top: 0;
  background-color: white;
}
.dropdown-category-item {
  padding-left: 20px !important;
}
.dropdown-item {
  padding: 5px;
  cursor: pointer;
}
.dropdown-selected,
.dropdown-item:hover {
  background-color: lightgray;
}
.dropdown-icon {
  height: 50%;
  max-height: 20px;
  position: absolute;
  right: 10px;
}
.disabled-container {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}
.dropdown-search-input,
.dropdown-search-selected {
  border: none;
  background-color: transparent;
  height: 100%;
  width: 100%;
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 5px;
  padding: 0 5px;
  padding-right: 30px;

  &:focus {
    outline: none;
  }
}
.dropdown-search-selected {
  pointer-events: none;
  z-index: 2;
  background-color: white;
  display: flex;
  align-items: center;
  width: calc(100% - 30px);
}
</style>
